import { useTranslation } from 'react-i18next'
import { Badge, Card, CardBody, CardHeader, CardTitle, cn } from '@ludus/ui'
import type { CardFact } from '../../types.ts'
import type { Interval } from '../CardView.tsx'
import { timecode } from '../Transcript/TranscriptPanel.tsx'

interface FactsPanelProps {
  facts: CardFact[]
  selection: Interval | null
  onSelect: (interval: Interval | null) => void
}

/** Facts whose selection highlights their evidence interval in the transcript and seeks the audio. */
export function FactsPanel({ facts, selection, onSelect }: FactsPanelProps) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('pages.audits.card.facts.title', { defaultValue: 'Facts' })}</CardTitle>
      </CardHeader>
      <CardBody>
        {facts.length === 0 ? (
          <p className="text-xs text-muted">
            {t('pages.audits.card.facts.none', { defaultValue: 'No facts extracted yet.' })}
          </p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {facts.map((fact) => {
              const span = fact.evidence.find((e) => e.startMs !== null && e.endMs !== null)
              const interval =
                span && span.startMs !== null && span.endMs !== null
                  ? { startMs: span.startMs, endMs: span.endMs }
                  : null
              const active =
                interval !== null &&
                selection !== null &&
                selection.startMs === interval.startMs &&
                selection.endMs === interval.endMs
              return (
                <li key={fact.id}>
                  <button
                    type="button"
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 rounded px-1 py-1 text-left hover:bg-background-secondary',
                      active && 'bg-primary-light',
                    )}
                    onClick={() => onSelect(active ? null : interval)}
                  >
                    <span className="flex flex-wrap items-center gap-1">
                      <span className="text-xs text-muted">{fact.key}</span>
                      <span className="font-medium text-foreground">{fact.value}</span>
                      <Badge
                        tone={
                          fact.kind === 'fact'
                            ? 'success'
                            : fact.kind === 'hypothesis'
                              ? 'warning'
                              : 'info'
                        }
                        size="sm"
                      >
                        {t(`pages.audits.card.facts.kind.${fact.kind}`, {
                          defaultValue: fact.kind,
                        })}
                      </Badge>
                      {fact.confidence !== null && (
                        <span className="text-[11px] text-muted">{fact.confidence}%</span>
                      )}
                    </span>
                    {fact.evidence.length === 0 ? (
                      <span className="text-[11px] text-toast-warning-text">
                        {t('pages.audits.card.facts.noEvidence', {
                          defaultValue: 'no evidence — a hypothesis',
                        })}
                      </span>
                    ) : (
                      fact.evidence.map((e) => (
                        <span key={e.id} className="text-[11px] text-muted">
                          {e.startMs !== null && e.endMs !== null
                            ? `${timecode(e.startMs)}–${timecode(e.endMs)} `
                            : ''}
                          {e.quote ? `«${e.quote}»` : e.kind}
                        </span>
                      ))
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}
